import { useEffect, useLayoutEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  ArrowDown,
  ArrowUp,
  FileText,
  GripVertical,
  Heading,
  Image as ImageIcon,
  LayoutGrid,
  Minus,
  Monitor,
  MousePointerClick,
  MoveVertical,
  Plus,
  Receipt,
  Send,
  ShoppingCart,
  Smartphone,
  Trash2,
  Type,
} from 'lucide-react';
import { Badge, Button, Dialog, EmptyState, Field, Input, Section, Select, Spinner, Textarea } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import {
  EditorLanguages,
  LocalePicker,
  TranslationLinks,
  useContentLocale,
} from '../components/ContentLocale';
import { ContentReviewPanel, reviewTone } from '../components/ContentReview';
import { ImageUpload } from '../components/ImageUpload';
import { ProductMultiPicker } from '../components/ProductMultiPicker';
import { api, ApiError, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  Category,
  CollectionRow,
  EmailBlock,
  EmailTemplateDetail,
  EmailTemplatePreview,
  EmailTemplateRow,
} from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';

/**
 * Pazarlama → E-posta şablonları (F6-05, ADR-086): blok tabanlı düzenleyici, canlı önizleme (masaüstü/telefon,
 * düz metin), yalnızca panel kullanıcılarına test gönderimi, çeviri ve sağlık beyanı incelemesi. HTML sunucuda
 * üretilir; önizleme panelin kendi kaynağından görsel yükler ve betik çalıştırmaz (sandbox).
 */

type BlockType = EmailBlock['type'];
const BLOCK_TYPES: Array<{ type: BlockType; icon: ReactNode }> = [
  { type: 'heading', icon: <Heading className="h-4 w-4" aria-hidden /> },
  { type: 'text', icon: <Type className="h-4 w-4" aria-hidden /> },
  { type: 'image', icon: <ImageIcon className="h-4 w-4" aria-hidden /> },
  { type: 'button', icon: <MousePointerClick className="h-4 w-4" aria-hidden /> },
  { type: 'products', icon: <LayoutGrid className="h-4 w-4" aria-hidden /> },
  { type: 'divider', icon: <Minus className="h-4 w-4" aria-hidden /> },
  { type: 'spacer', icon: <MoveVertical className="h-4 w-4" aria-hidden /> },
  { type: 'cart', icon: <ShoppingCart className="h-4 w-4" aria-hidden /> },
  { type: 'order', icon: <Receipt className="h-4 w-4" aria-hidden /> },
  { type: 'quote', icon: <FileText className="h-4 w-4" aria-hidden /> },
];
const BLOCK_LIMIT = 40;
/** Kişiselleştirme alanlarının yazımı (dil bağımsız sözdizimi; açıklamaları çeviriden gelir). */
const MERGE_FIELDS = ['firstName', 'name', 'email', 'storeName'] as const;

const newBlockId = () => `b${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

function newBlock(type: BlockType): EmailBlock {
  const id = newBlockId();
  switch (type) {
    case 'heading':
      return { id, type, text: '', level: 1, align: 'left' };
    case 'text':
      return { id, type, text: '', align: 'left' };
    case 'image':
      return { id, type, fileId: '', alt: '', href: null, width: 'full' };
    case 'button':
      return { id, type, label: '', href: '', align: 'center' };
    case 'products':
      return { id, type, source: { kind: 'newest', limit: 4 }, columns: 2, showPrice: true };
    case 'spacer':
      return { id, type, size: 'md' };
    default:
      return { id, type } as EmailBlock;
  }
}

/** Kaydedilmeden önce doldurulması gereken alan (eksikse çeviri anahtarı). Eksik blok önizlemeye girmez. */
function missingOf(b: EmailBlock): string | null {
  switch (b.type) {
    case 'heading':
    case 'text':
      return b.text.trim() ? null : 'text';
    case 'image':
      return b.fileId ? null : 'image';
    case 'button':
      return !b.label.trim() ? 'label' : !b.href.trim() ? 'link' : null;
    case 'products': {
      const s = b.source;
      if (s.kind === 'manual') return s.productIds.length ? null : 'products';
      if (s.kind === 'collection') return s.collectionId ? null : 'collection';
      if (s.kind === 'category') return s.categoryId ? null : 'category';
      return null;
    }
    default:
      return null;
  }
}

function useDebounced(value: string, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setV(value), ms);
    return () => clearTimeout(h);
  }, [value, ms]);
  return v;
}

// ---------------------------------------------------------------------------- Liste

export function EmailTemplatesPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const { locales, locale, setLocale } = useContentLocale(storeId);
  const list = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => api.get<EmailTemplateRow[]>('/v1/admin/email-templates'),
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  const all = list.data ?? [];
  const rows = all.filter((r) => r.storeId === storeId && (!locale || r.locale === locale));
  const create = can('marketing.campaign.write') && storeId && (
    <div className="flex items-center gap-2">
      <Button
        variant="primary"
        onClick={() => void navigate({ to: '/e-posta-sablonlari/$id', params: { id: 'yeni' } })}
      >
        <Plus className="h-4 w-4" aria-hidden />
        {t('admin.emailTemplates.create')}
      </Button>
      <HelpTip k="email_templates.create" />
    </div>
  );
  return (
    <PageFrame helpKey="page.email_templates" actions={create}>
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <div className="space-y-6">
          <p className="max-w-[70ch] text-sm text-ink-2">{t('admin.emailTemplates.lead')}</p>
          <div className="flex flex-wrap items-end gap-4">
            <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
            <LocalePicker locales={locales} locale={locale} onChange={setLocale} />
          </div>
          {!list.data ? (
            <Spinner label={t('common.loading')} />
          ) : !rows.length ? (
            <EmptyState
              title={t('admin.emailTemplates.emptyTitle')}
              description={t('admin.emailTemplates.emptyLead')}
            />
          ) : (
            <div className="overflow-x-auto rounded-md border border-line">
              <table className="w-full text-sm" aria-label={t('admin.emailTemplates.listLabel')}>
                <thead className="bg-surface-2 text-ink-2">
                  <tr>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.emailTemplates.colName')}</th>
                    <th className="px-3 py-2 text-start font-medium">
                      {t('admin.emailTemplates.colReview')}
                    </th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.emailTemplates.colUpdated')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-line align-top">
                      <td className="px-3 py-2">
                        <Link
                          to="/e-posta-sablonlari/$id"
                          params={{ id: r.id }}
                          className="font-medium text-pine hover:underline"
                        >
                          {r.name}
                        </Link>
                        <p className="text-ink-3">{r.subject}</p>
                        <TranslationLinks
                          items={all.filter(
                            (x) => x.translationGroupId === r.translationGroupId && x.id !== r.id,
                          )}
                          to="/e-posta-sablonlari/$id"
                        />
                      </td>
                      <td className="px-3 py-2">
                        {r.reviewRequired || r.reviewStatus !== 'not_required' ? (
                          <Badge tone={reviewTone[r.reviewStatus]}>
                            {t(`admin.pages.reviews.${r.reviewStatus}`)}
                          </Badge>
                        ) : (
                          <span className="text-ink-3">{t('admin.emailTemplates.noReview')}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-end text-ink-2">{formatDate(r.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </PageFrame>
  );
}

// ---------------------------------------------------------------------------- Düzenleyici

interface Draft {
  name: string;
  subject: string;
  preheader: string;
  blocks: EmailBlock[];
}

const draftOf = (d: EmailTemplateDetail): Draft => ({
  name: d.name,
  subject: d.subject,
  preheader: d.preheader,
  blocks: d.blocks,
});

export function EmailTemplateEditorPage() {
  const { id } = useParams({ from: '/app/e-posta-sablonlari/$id' });
  const isNew = id === 'yeni';
  const t = useT();
  const q = useQuery({
    queryKey: ['email-template', id],
    queryFn: () => api.get<EmailTemplateDetail>(`/v1/admin/email-templates/${id}`),
    enabled: !isNew,
  });
  if (!isNew && !q.data) return <Spinner label={t('common.loading')} />;
  return <TemplateEditor key={q.data?.id ?? 'yeni'} template={q.data ?? null} />;
}

function TemplateEditor({ template }: { template: EmailTemplateDetail | null }) {
  const t = useT();
  const { formatDate } = useI18n();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can('marketing.campaign.write');
  const { stores, storeId: chosenStore, setStoreId } = useStoreChoice();
  const storeId = template?.storeId ?? chosenStore;
  const { locales, locale: chosenLocale, setLocale } = useContentLocale(template ? null : storeId);
  const locale = template?.locale ?? chosenLocale;
  const [draft, setDraft] = useState<Draft>(() =>
    template ? draftOf(template) : { name: '', subject: '', preheader: '', blocks: [] },
  );
  useEffect(() => {
    if (template) setDraft(draftOf(template));
  }, [template]);
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const setBlock = (i: number, b: EmailBlock) =>
    setDraft((d) => ({ ...d, blocks: d.blocks.map((x, j) => (j === i ? b : x)) }));
  const dirty = !template || JSON.stringify(draft) !== JSON.stringify(draftOf(template));
  const missing = draft.blocks.map(missingOf);
  const complete = draft.name.trim().length >= 2 && !!draft.subject.trim() && missing.every((m) => !m);

  // Canlı önizleme: eksik bloklar atlanır; hata alanları özgün blok sırasına geri çevrilir.
  const indexMap = draft.blocks.map((_, i) => i).filter((i) => !missing[i]);
  const previewKey = useDebounced(
    JSON.stringify({
      body: {
        storeId,
        locale,
        subject: draft.subject,
        preheader: draft.preheader,
        blocks: indexMap.map((i) => draft.blocks[i]),
      },
      indexMap,
    }),
    500,
  );
  const previewReq = JSON.parse(previewKey) as {
    body: { storeId: string; locale: string | null; subject: string };
    indexMap: number[];
  };
  const preview = useQuery({
    queryKey: ['email-template-preview', previewKey],
    queryFn: () => api.post<EmailTemplatePreview>('/v1/admin/email-templates/preview', previewReq.body),
    enabled: !!previewReq.body.storeId && !!previewReq.body.locale && !!previewReq.body.subject.trim(),
    placeholderData: keepPreviousData,
    retry: false,
  });

  const save = useApiMutation(
    () =>
      template
        ? api.patch<EmailTemplateDetail>(`/v1/admin/email-templates/${template.id}`, {
            ...draft,
            version: template.version,
          })
        : api.post<EmailTemplateDetail>('/v1/admin/email-templates', {
            ...draft,
            storeId,
            locale: locale ?? undefined,
          }),
    {
      invalidate: [['email-templates'], ...(template ? [['email-template', template.id]] : [])],
      success: t('admin.emailTemplates.saved'),
      onSuccess: (r) => {
        if (!template) void navigate({ to: '/e-posta-sablonlari/$id', params: { id: r.id } });
      },
    },
  );
  const review = useApiMutation(
    (v: { decision: 'approved' | 'rejected'; note: string }) =>
      api.post<EmailTemplateDetail>(`/v1/admin/email-templates/${template!.id}/review`, {
        decision: v.decision,
        note: v.note || null,
        contentHash: template!.review.contentHash,
        version: template!.version,
      }),
    {
      invalidate: [['email-templates'], ['email-template', template?.id]],
      success: t('admin.pages.reviewedToast'),
    },
  );
  const remove = useApiMutation(() => api.del(`/v1/admin/email-templates/${template!.id}`), {
    invalidate: [['email-templates']],
    success: t('admin.emailTemplates.deleted'),
    onSuccess: () => void navigate({ to: '/e-posta-sablonlari' }),
  });
  const translate = useApiMutation(
    (l: string) =>
      api.post<EmailTemplateDetail>(`/v1/admin/email-templates/${template!.id}/translations`, {
        locale: l,
      }),
    {
      invalidate: [['email-templates'], ['email-template', template?.id]],
      success: t('admin.translations.created'),
      silentFieldErrors: true,
      onSuccess: (r) => void navigate({ to: '/e-posta-sablonlari/$id', params: { id: r.id } }),
    },
  );

  const fe = (f: string) => fieldError(save.error, f);
  const blockError = (i: number, f: string) => {
    const own = fieldError(save.error, `blocks.${i}.${f}`);
    if (own) return own;
    const j = previewReq.indexMap.indexOf(i);
    return j >= 0 ? fieldError(preview.error, `blocks.${j}.${f}`) : undefined;
  };

  // Sıralama: sürükle-bırak ve (klavye/ekran okuyucu için) yukarı/aşağı düğmeleri.
  const [announce, setAnnounce] = useState('');
  const move = (from: number, to: number) => {
    if (from === to || to < 0 || to >= draft.blocks.length) return;
    setDraft((d) => {
      const blocks = [...d.blocks];
      const [b] = blocks.splice(from, 1);
      blocks.splice(to, 0, b!);
      return { ...d, blocks };
    });
    setAnnounce(t('admin.emailTemplates.moved', { position: to + 1 }));
  };
  const dragFrom = useRef<number | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);

  return (
    <PageFrame
      wide
      helpKey="page.email_template_editor"
      title={template ? template.name : t('admin.emailTemplates.create')}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {template && canWrite && <TestSend template={template} dirty={dirty} />}
          {canWrite && (
            <>
              <Button
                variant="primary"
                onClick={() => save.mutate(undefined)}
                disabled={!dirty || !complete || !storeId || !locale || save.isPending}
              >
                {template ? t('admin.emailTemplates.save') : t('admin.emailTemplates.createConfirm')}
              </Button>
              <HelpTip k="email_templates.save" />
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Link to="/e-posta-sablonlari" className="text-pine hover:underline">
          {t('admin.emailTemplates.back')}
        </Link>
        {template && (template.review.required || template.review.status !== 'not_required') && (
          <Badge tone={reviewTone[template.review.status]}>
            {t(`admin.pages.reviews.${template.review.status}`)}
          </Badge>
        )}
        {template && dirty && <Badge tone="amber">{t('admin.emailTemplates.unsaved')}</Badge>}
        {canWrite && dirty && !complete && (
          <span className="text-ink-2">{t('admin.emailTemplates.completeFirst')}</span>
        )}
      </div>
      {template ? (
        <EditorLanguages
          storeId={template.storeId}
          locale={template.locale}
          translations={template.translations}
          to="/e-posta-sablonlari/$id"
          canTranslate={canWrite}
          pending={translate.isPending}
          error={fieldError(translate.error, 'locale')}
          onCreate={(l) => translate.mutate(l)}
        />
      ) : (
        <div className="flex flex-wrap items-end gap-4">
          <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
          <LocalePicker locales={locales} locale={chosenLocale} onChange={setLocale} />
        </div>
      )}

      <div className="mt-6 grid gap-8 xl:grid-cols-2 2xl:grid-cols-[minmax(0,1fr)_minmax(0,44rem)]">
        <fieldset disabled={!canWrite} className="min-w-0 space-y-8">
          <legend className="sr-only">{t('admin.emailTemplates.editor')}</legend>
          <Section title={t('admin.emailTemplates.basics')} className="border-t-0 pt-0">
            <div className="grid gap-4">
              <Field
                label={t('admin.emailTemplates.name')}
                description={t('admin.emailTemplates.nameLead')}
                help={<HelpTip k="email_templates.name" />}
                error={fe('name')}
                required
              >
                {(p) => (
                  <Input
                    {...p}
                    value={draft.name}
                    maxLength={120}
                    onChange={(e) => set({ name: e.target.value })}
                  />
                )}
              </Field>
              <Field
                label={t('admin.emailTemplates.subject')}
                description={t('admin.pages.chars', { count: draft.subject.length, max: 200 })}
                help={<HelpTip k="email_templates.subject" />}
                error={fe('subject')}
                required
              >
                {(p) => (
                  <Input
                    {...p}
                    value={draft.subject}
                    maxLength={200}
                    onChange={(e) => set({ subject: e.target.value })}
                  />
                )}
              </Field>
              <Field
                label={t('admin.emailTemplates.preheader')}
                optionalLabel={t('common.optional')}
                description={t('admin.emailTemplates.preheaderLead')}
                help={<HelpTip k="email_templates.preheader" />}
                error={fe('preheader')}
              >
                {(p) => (
                  <Input
                    {...p}
                    value={draft.preheader}
                    maxLength={200}
                    onChange={(e) => set({ preheader: e.target.value })}
                  />
                )}
              </Field>
              <div className="rounded-md border border-line bg-surface-2 px-3 py-2 text-sm">
                <p className="flex items-center gap-1.5 font-medium">
                  {t('admin.emailTemplates.mergeTitle')}
                  <HelpTip k="email_templates.merge_fields" />
                </p>
                <ul className="mt-1 grid gap-0.5 text-ink-2 sm:grid-cols-2">
                  {MERGE_FIELDS.map((f) => (
                    <li key={f}>
                      <code className="rounded-sm bg-surface px-1 text-ink">{`{{${f}}}`}</code>{' '}
                      {t(`admin.emailTemplates.mergeFields.${f}`)}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-ink-3">
                  {t('admin.emailTemplates.mergeFallback')}{' '}
                  <code className="rounded-sm bg-surface px-1 text-ink">{'{{firstName|…}}'}</code>
                </p>
              </div>
            </div>
          </Section>

          <Section
            title={t('admin.emailTemplates.blocks')}
            description={t('admin.emailTemplates.blocksLead')}
            action={<HelpTip k="email_templates.blocks" />}
          >
            <p className="sr-only" aria-live="polite">
              {announce}
            </p>
            {!draft.blocks.length ? (
              <p className="mb-4 text-sm text-ink-3">{t('admin.emailTemplates.noBlocks')}</p>
            ) : (
              <ol className="mb-4 space-y-3" aria-label={t('admin.emailTemplates.blocks')}>
                {draft.blocks.map((b, i) => (
                  <li
                    key={b.id}
                    data-testid={`email-block-${i}`}
                    onDragEnter={(e: DragEvent) => {
                      if (dragFrom.current !== null) e.preventDefault();
                    }}
                    onDragOver={(e: DragEvent) => {
                      if (dragFrom.current === null) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      setDropAt(i);
                    }}
                    onDragLeave={() => setDropAt((x) => (x === i ? null : x))}
                    onDrop={(e: DragEvent) => {
                      e.preventDefault();
                      if (dragFrom.current !== null) move(dragFrom.current, i);
                      dragFrom.current = null;
                      setDropAt(null);
                    }}
                    className={`rounded-md border bg-surface ${dropAt === i ? 'border-pine ring-2 ring-pine/30' : 'border-line'}`}
                  >
                    <div className="flex items-center gap-2 border-b border-line px-3 py-2">
                      {canWrite && (
                        <span
                          draggable
                          aria-hidden
                          title={t('admin.emailTemplates.dragHint')}
                          data-testid={`email-block-drag-${i}`}
                          onDragStart={(e) => {
                            dragFrom.current = i;
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', b.id);
                            const li = (e.currentTarget as HTMLElement).closest('li');
                            if (li) e.dataTransfer.setDragImage(li, 24, 16);
                          }}
                          onDragEnd={() => {
                            dragFrom.current = null;
                            setDropAt(null);
                          }}
                          className="cursor-grab text-ink-3 active:cursor-grabbing"
                        >
                          <GripVertical className="h-4 w-4" />
                        </span>
                      )}
                      <span id={`blk-${b.id}`} className="text-sm font-medium">
                        {t('admin.emailTemplates.blockTitle', {
                          n: i + 1,
                          type: t(`admin.emailTemplates.blockTypes.${b.type}`),
                        })}
                      </span>
                      {missing[i] && (
                        <span className="text-[0.8125rem] text-amber">
                          {t(`admin.emailTemplates.missing.${missing[i]}`)}
                        </span>
                      )}
                      {canWrite && (
                        <span className="ms-auto flex items-center gap-0.5">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={i === 0}
                            onClick={() => move(i, i - 1)}
                            aria-label={t('admin.emailTemplates.moveUp', { n: i + 1 })}
                          >
                            <ArrowUp className="h-4 w-4" aria-hidden />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={i === draft.blocks.length - 1}
                            onClick={() => move(i, i + 1)}
                            aria-label={t('admin.emailTemplates.moveDown', { n: i + 1 })}
                          >
                            <ArrowDown className="h-4 w-4" aria-hidden />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              set({ blocks: draft.blocks.filter((_, j) => j !== i) });
                              setAnnounce(t('admin.emailTemplates.removed', { n: i + 1 }));
                            }}
                            aria-label={t('admin.emailTemplates.removeBlock', { n: i + 1 })}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </Button>
                          {i === 0 && <HelpTip k="email_templates.move" />}
                        </span>
                      )}
                    </div>
                    <div role="group" aria-labelledby={`blk-${b.id}`} className="@container p-3">
                      <BlockFields
                        block={b}
                        onChange={(nb) => setBlock(i, nb)}
                        error={(f) => blockError(i, f)}
                      />
                    </div>
                  </li>
                ))}
              </ol>
            )}
            {canWrite && (
              <div className="rounded-md border border-dashed border-line-strong p-3">
                <p className="mb-2 flex items-center gap-1.5 text-sm font-medium">
                  {t('admin.emailTemplates.addBlock')}
                  <HelpTip k="email_templates.add_block" />
                </p>
                <div className="flex flex-wrap gap-2">
                  {BLOCK_TYPES.map((bt) => (
                    <Button
                      key={bt.type}
                      size="sm"
                      disabled={draft.blocks.length >= BLOCK_LIMIT}
                      onClick={() => {
                        set({ blocks: [...draft.blocks, newBlock(bt.type)] });
                        setAnnounce(
                          t('admin.emailTemplates.added', {
                            type: t(`admin.emailTemplates.blockTypes.${bt.type}`),
                            n: draft.blocks.length + 1,
                          }),
                        );
                      }}
                    >
                      {bt.icon}
                      {t(`admin.emailTemplates.blockTypes.${bt.type}`)}
                    </Button>
                  ))}
                </div>
                {draft.blocks.length >= BLOCK_LIMIT && (
                  <p className="mt-2 text-sm text-ink-2">
                    {t('admin.emailTemplates.blockLimit', { max: BLOCK_LIMIT })}
                  </p>
                )}
              </div>
            )}
          </Section>
        </fieldset>

        <div className="min-w-0 space-y-4 xl:sticky xl:top-4 xl:self-start">
          <PreviewPane
            data={preview.data}
            error={preview.error instanceof ApiError ? preview.error : null}
            loading={preview.isFetching}
            waiting={!draft.subject.trim()}
            blocks={draft.blocks}
          />
        </div>
      </div>

      {template && (
        <ContentReviewPanel
          status={template.review.status}
          note={template.review.note}
          findings={template.review.findings}
          required={template.review.required}
          dirty={dirty}
          canReview={can('content.expert_review')}
          busy={review.isPending}
          onDecide={(decision, note) => review.mutate({ decision, note })}
        />
      )}

      {template && canWrite && (
        <Section title={t('admin.emailTemplates.dangerZone')}>
          <div className="flex items-center gap-2">
            <ConfirmButton
              message={t('admin.emailTemplates.deleteConfirm', { name: template.name })}
              onConfirm={() => remove.mutateAsync(undefined)}
            >
              {t('common.delete')}
            </ConfirmButton>
            <HelpTip k="email_templates.delete" />
          </div>
        </Section>
      )}
      {template && (
        <p className="text-sm text-ink-3">
          {t('admin.emailTemplates.updated', { date: formatDate(template.updatedAt) })}
        </p>
      )}
    </PageFrame>
  );
}

// ---------------------------------------------------------------------------- Blok alanları

function AlignSelect({
  value,
  onChange,
}: {
  value: 'left' | 'center';
  onChange: (v: 'left' | 'center') => void;
}) {
  const t = useT();
  return (
    <Field label={t('admin.emailTemplates.align')} help={<HelpTip k="email_templates.align" />}>
      {(p) => (
        <Select {...p} value={value} onChange={(e) => onChange(e.target.value as 'left' | 'center')}>
          <option value="left">{t('admin.emailTemplates.aligns.left')}</option>
          <option value="center">{t('admin.emailTemplates.aligns.center')}</option>
        </Select>
      )}
    </Field>
  );
}

function BlockFields({
  block: b,
  onChange,
  error,
}: {
  block: EmailBlock;
  onChange: (b: EmailBlock) => void;
  error: (field: string) => string | undefined;
}) {
  const t = useT();
  const { can } = usePermissions();
  const cats = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/v1/admin/categories'),
    enabled: b.type === 'products' && can('catalog.product.read'),
  });
  const cols = useQuery({
    queryKey: ['collections'],
    queryFn: () => api.get<CollectionRow[]>('/v1/admin/collections'),
    enabled: b.type === 'products' && can('catalog.product.read'),
  });
  switch (b.type) {
    case 'heading':
      return (
        <div className="grid gap-3 @sm:grid-cols-2 @2xl:grid-cols-[minmax(0,1fr)_10rem_10rem]">
          <Field
            label={t('admin.emailTemplates.headingText')}
            help={<HelpTip k="email_templates.heading" />}
            error={error('text')}
            required
            className="@sm:col-span-2 @2xl:col-span-1"
          >
            {(p) => (
              <Input
                {...p}
                value={b.text}
                maxLength={200}
                onChange={(e) => onChange({ ...b, text: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('admin.emailTemplates.level')} help={<HelpTip k="email_templates.level" />}>
            {(p) => (
              <Select
                {...p}
                value={b.level}
                onChange={(e) => onChange({ ...b, level: Number(e.target.value) === 2 ? 2 : 1 })}
              >
                <option value={1}>{t('admin.emailTemplates.levels.1')}</option>
                <option value={2}>{t('admin.emailTemplates.levels.2')}</option>
              </Select>
            )}
          </Field>
          <AlignSelect value={b.align} onChange={(align) => onChange({ ...b, align })} />
        </div>
      );
    case 'text':
      return (
        <div className="grid gap-3">
          <Field
            label={t('admin.emailTemplates.text')}
            description={t('admin.emailTemplates.textLead')}
            help={<HelpTip k="email_templates.text" />}
            error={error('text')}
            required
          >
            {(p) => (
              <Textarea
                {...p}
                rows={5}
                value={b.text}
                maxLength={5000}
                onChange={(e) => onChange({ ...b, text: e.target.value })}
              />
            )}
          </Field>
          <div className="max-w-[12rem]">
            <AlignSelect value={b.align} onChange={(align) => onChange({ ...b, align })} />
          </div>
        </div>
      );
    case 'image':
      return (
        <div className="grid gap-3">
          <div>
            <ImageUpload
              wide
              value={b.fileId || null}
              label={
                b.fileId ? t('admin.emailTemplates.replaceImage') : t('admin.emailTemplates.uploadImage')
              }
              help={<HelpTip k="email_templates.image" />}
              onChange={(fileId) => onChange({ ...b, fileId: fileId ?? '' })}
            />
            {error('fileId') && (
              <p role="alert" className="mt-1 text-[0.8125rem] font-medium text-rose">
                {error('fileId')}
              </p>
            )}
          </div>
          <div className="grid gap-3 @sm:grid-cols-2">
            <Field
              label={t('admin.emailTemplates.imageAlt')}
              description={t('admin.emailTemplates.imageAltLead')}
              help={<HelpTip k="email_templates.image_alt" />}
              error={error('alt')}
              required={!!b.href}
              optionalLabel={t('common.optional')}
            >
              {(p) => (
                <Input
                  {...p}
                  value={b.alt}
                  maxLength={300}
                  onChange={(e) => onChange({ ...b, alt: e.target.value })}
                />
              )}
            </Field>
            <Field
              label={t('admin.emailTemplates.imageLink')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="email_templates.link" />}
              error={error('href')}
            >
              {(p) => (
                <Input
                  {...p}
                  value={b.href ?? ''}
                  placeholder="/kampanya"
                  spellCheck={false}
                  onChange={(e) => onChange({ ...b, href: e.target.value || null })}
                />
              )}
            </Field>
            <Field label={t('admin.emailTemplates.width')} help={<HelpTip k="email_templates.width" />}>
              {(p) => (
                <Select
                  {...p}
                  value={b.width}
                  onChange={(e) => onChange({ ...b, width: e.target.value === 'half' ? 'half' : 'full' })}
                >
                  <option value="full">{t('admin.emailTemplates.widths.full')}</option>
                  <option value="half">{t('admin.emailTemplates.widths.half')}</option>
                </Select>
              )}
            </Field>
          </div>
        </div>
      );
    case 'button':
      return (
        <div className="grid gap-3 @sm:grid-cols-2 @2xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_10rem]">
          <Field
            label={t('admin.emailTemplates.buttonLabel')}
            help={<HelpTip k="email_templates.button_label" />}
            error={error('label')}
            required
          >
            {(p) => (
              <Input
                {...p}
                value={b.label}
                maxLength={80}
                onChange={(e) => onChange({ ...b, label: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.emailTemplates.buttonLink')}
            help={<HelpTip k="email_templates.link" />}
            error={error('href')}
            required
          >
            {(p) => (
              <Input
                {...p}
                value={b.href}
                placeholder="/kampanya"
                spellCheck={false}
                onChange={(e) => onChange({ ...b, href: e.target.value })}
              />
            )}
          </Field>
          <AlignSelect value={b.align} onChange={(align) => onChange({ ...b, align })} />
        </div>
      );
    case 'products': {
      const s = b.source;
      const setSource = (kind: string) => {
        const limit = 'limit' in s ? s.limit : 4;
        onChange({
          ...b,
          source:
            kind === 'manual'
              ? { kind: 'manual', productIds: [] }
              : kind === 'collection'
                ? { kind: 'collection', collectionId: '', limit }
                : kind === 'category'
                  ? { kind: 'category', categoryId: '', limit }
                  : { kind: 'newest', limit },
        });
      };
      return (
        <div className="grid gap-3">
          <div className="grid gap-3 @sm:grid-cols-2 @2xl:grid-cols-3">
            <Field
              label={t('admin.emailTemplates.source')}
              help={<HelpTip k="email_templates.products_source" />}
              error={error('source')}
            >
              {(p) => (
                <Select {...p} value={s.kind} onChange={(e) => setSource(e.target.value)}>
                  {(['newest', 'collection', 'category', 'manual'] as const).map((k) => (
                    <option key={k} value={k}>
                      {t(`admin.emailTemplates.sources.${k}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('admin.emailTemplates.columns')} help={<HelpTip k="email_templates.columns" />}>
              {(p) => (
                <Select
                  {...p}
                  value={b.columns}
                  onChange={(e) => {
                    const c = Number(e.target.value);
                    onChange({ ...b, columns: c === 1 ? 1 : c === 3 ? 3 : 2 });
                  }}
                >
                  {[1, 2, 3].map((c) => (
                    <option key={c} value={c}>
                      {t('admin.emailTemplates.columnCount', { count: c })}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {s.kind !== 'manual' && (
              <Field label={t('admin.emailTemplates.limit')} help={<HelpTip k="email_templates.limit" />}>
                {(p) => (
                  <Input
                    {...p}
                    type="number"
                    min={1}
                    max={12}
                    value={s.limit}
                    onChange={(e) =>
                      onChange({
                        ...b,
                        source: { ...s, limit: Math.max(1, Math.min(12, Number(e.target.value) || 4)) },
                      })
                    }
                  />
                )}
              </Field>
            )}
          </div>
          {s.kind === 'collection' && (
            <Field
              label={t('admin.emailTemplates.collection')}
              help={<HelpTip k="email_templates.collection" />}
              className="max-w-md"
              required
            >
              {(p) => (
                <Select
                  {...p}
                  value={s.collectionId}
                  onChange={(e) => onChange({ ...b, source: { ...s, collectionId: e.target.value } })}
                >
                  <option value="">{t('admin.emailTemplates.choose')}</option>
                  {(cols.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          {s.kind === 'category' && (
            <Field
              label={t('admin.emailTemplates.category')}
              help={<HelpTip k="email_templates.category" />}
              className="max-w-md"
              required
            >
              {(p) => (
                <Select
                  {...p}
                  value={s.categoryId}
                  onChange={(e) => onChange({ ...b, source: { ...s, categoryId: e.target.value } })}
                >
                  <option value="">{t('admin.emailTemplates.choose')}</option>
                  {(cats.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          {s.kind === 'manual' && (
            <ProductMultiPicker
              value={s.productIds}
              max={12}
              label={t('admin.emailTemplates.pickProducts')}
              help={<HelpTip k="email_templates.products_pick" />}
              onChange={(productIds) => onChange({ ...b, source: { kind: 'manual', productIds } })}
            />
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={b.showPrice}
              onChange={(e) => onChange({ ...b, showPrice: e.target.checked })}
            />
            {t('admin.emailTemplates.showPrice')}
            <HelpTip k="email_templates.show_price" />
          </label>
          <p className="text-[0.8125rem] text-ink-3">{t('admin.emailTemplates.productsLead')}</p>
        </div>
      );
    }
    case 'spacer':
      return (
        <Field
          label={t('admin.emailTemplates.spacerSize')}
          help={<HelpTip k="email_templates.spacer" />}
          className="max-w-[12rem]"
        >
          {(p) => (
            <Select
              {...p}
              value={b.size}
              onChange={(e) => onChange({ ...b, size: e.target.value as 'sm' | 'md' | 'lg' })}
            >
              {(['sm', 'md', 'lg'] as const).map((x) => (
                <option key={x} value={x}>
                  {t(`admin.emailTemplates.sizes.${x}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      );
    case 'cart':
    case 'order':
    case 'quote':
      return (
        <p className="flex items-center gap-1.5 text-sm text-ink-2">
          {t(`admin.emailTemplates.${b.type}Lead`)}
          <HelpTip
            k={
              b.type === 'cart'
                ? 'email_templates.cart'
                : b.type === 'order'
                  ? 'email_templates.order'
                  : 'email_templates.quote'
            }
          />
        </p>
      );
    default:
      return <p className="text-sm text-ink-2">{t('admin.emailTemplates.dividerLead')}</p>;
  }
}

// ---------------------------------------------------------------------------- Önizleme

function PreviewPane({
  data,
  error,
  loading,
  waiting,
  blocks,
}: {
  data: EmailTemplatePreview | undefined;
  error: ApiError | null;
  loading: boolean;
  waiting: boolean;
  blocks: EmailBlock[];
}) {
  const t = useT();
  const [device, setDevice] = useState<'desktop' | 'phone'>('desktop');
  const [mode, setMode] = useState<'html' | 'text'>('html');
  // Bağlantılar önizleme çerçevesinde değil yeni sekmede açılır.
  const doc = data?.html.replace(/<head>/i, '<head><base target="_blank">') ?? '';
  const empty = (data?.emptyProductBlocks ?? [])
    .map((id) => blocks.findIndex((b) => b.id === id) + 1)
    .filter((n) => n > 0);
  return (
    <section aria-labelledby="email-preview-h" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <h2 id="email-preview-h" className="font-cond text-lg font-semibold">
            {t('admin.emailTemplates.preview')}
          </h2>
          <HelpTip k="email_templates.preview" />
          {loading && <span className="text-sm text-ink-3">{t('admin.emailTemplates.previewUpdating')}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            role="group"
            aria-label={t('admin.emailTemplates.device')}
            className="flex rounded-sm border border-line"
          >
            {(
              [
                ['desktop', <Monitor key="d" className="h-4 w-4" aria-hidden />],
                ['phone', <Smartphone key="p" className="h-4 w-4" aria-hidden />],
              ] as const
            ).map(([d, icon]) => (
              <button
                key={d}
                type="button"
                aria-pressed={device === d}
                onClick={() => setDevice(d)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-ink-2 aria-pressed:bg-pine-tint aria-pressed:text-pine"
              >
                {icon}
                {t(`admin.emailTemplates.devices.${d}`)}
              </button>
            ))}
          </div>
          <div
            role="group"
            aria-label={t('admin.emailTemplates.format')}
            className="flex rounded-sm border border-line"
          >
            {(['html', 'text'] as const).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={mode === m}
                onClick={() => setMode(m)}
                className="px-2.5 py-1.5 text-sm text-ink-2 aria-pressed:bg-pine-tint aria-pressed:text-pine"
              >
                {t(`admin.emailTemplates.formats.${m}`)}
              </button>
            ))}
          </div>
          <HelpTip k="email_templates.device" />
        </div>
      </div>
      {error && (
        <p role="alert" className="rounded-md border border-rose/30 bg-rose-tint px-3 py-2 text-sm text-rose">
          {error.errors.length ? t('admin.emailTemplates.previewFieldErrors') : error.title}
        </p>
      )}
      {waiting ? (
        <p className="rounded-md border border-dashed border-line-strong px-4 py-10 text-center text-sm text-ink-3">
          {t('admin.emailTemplates.previewWaiting')}
        </p>
      ) : !data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <div className="rounded-md border border-line bg-surface px-3 py-2 text-sm">
            <p className="font-medium text-ink">{data.subject}</p>
            {data.preheader && <p className="truncate text-ink-3">{data.preheader}</p>}
          </div>
          {data.findings.length > 0 && (
            <div className="rounded-md border border-amber/30 bg-amber-tint p-3 text-sm">
              <p className="font-medium">
                {t('admin.emailTemplates.findingsTitle', { count: data.findings.length })}
              </p>
              <ul className="mt-1 list-disc space-y-0.5 ps-5">
                {data.findings.map((f, i) => (
                  <li key={i}>
                    <strong>{f.match}</strong> — {t(`healthClaims.${f.code}`)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {empty.length > 0 && (
            <p className="rounded-md border border-amber/30 bg-amber-tint px-3 py-2 text-sm">
              {t('admin.emailTemplates.emptyProducts', { blocks: empty.join(', ') })}
            </p>
          )}
          {mode === 'html' ? (
            <div className="rounded-md border border-line bg-surface-2 p-2">
              <EmailFrame
                html={doc}
                width={DEVICE_WIDTH[device]}
                title={t('admin.emailTemplates.previewFrame')}
              />
            </div>
          ) : (
            <pre
              role="region"
              tabIndex={0}
              aria-label={t('admin.emailTemplates.formats.text')}
              className="max-h-[70vh] overflow-auto whitespace-pre-wrap rounded-md border border-line bg-surface p-3 font-sans text-sm text-ink"
            >
              {data.text}
            </pre>
          )}
        </>
      )}
    </section>
  );
}

/** Önizleme genişlikleri: masaüstü e-posta istemcisi (600 px gövde + kenar), telefon. */
const DEVICE_WIDTH = { desktop: 680, phone: 375 } as const;

/**
 * E-posta çerçevesi: seçilen genişlikte çizilir, sütuna sığmazsa orantılı küçültülür (e-postanın masaüstü düzeni
 * korunur). Çerçeve betik çalıştırmaz (`sandbox`); bağlantılar yeni sekmede açılır.
 */
function EmailFrame({ html, width, title }: { html: string; width: number; title: string }) {
  const outer = useRef<HTMLDivElement>(null);
  const [avail, setAvail] = useState(width);
  useLayoutEffect(() => {
    const el = outer.current;
    if (!el) return;
    const measure = () => setAvail(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const scale = Math.min(1, avail / width);
  const height = Math.max(448, Math.round(window.innerHeight * 0.7));
  return (
    <div ref={outer} className="relative overflow-hidden" style={{ height }}>
      <iframe
        title={title}
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        srcDoc={html}
        className="absolute top-0 rounded-sm border border-line bg-white"
        style={{
          left: Math.max(0, (avail - width * scale) / 2),
          width,
          height: height / scale,
          transform: scale < 1 ? `scale(${scale})` : undefined,
          transformOrigin: 'top left',
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------- Test gönderimi

function TestSend({ template, dirty }: { template: EmailTemplateDetail; dirty: boolean }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState('');
  const idem = useRef(new IdempotencyKey());
  const recipients = to
    .split(/[\s,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);
  const send = useApiMutation(
    () => {
      const body = { to: recipients };
      return api.post<{ sent: number }>(`/v1/admin/email-templates/${template.id}/test`, body, {
        idempotencyKey: idem.current.for(JSON.stringify(body)),
      });
    },
    {
      success: (r) => t('admin.emailTemplates.testQueued', { count: r.sent }),
      silentFieldErrors: true,
      onSuccess: () => setOpen(false),
    },
  );
  return (
    <>
      <span className="inline-flex items-center gap-1">
        <Button
          onClick={() => {
            idem.current = new IdempotencyKey();
            send.reset();
            setOpen(true);
          }}
        >
          <Send className="h-4 w-4" aria-hidden />
          {t('admin.emailTemplates.test')}
        </Button>
        <HelpTip k="email_templates.test_send" />
      </span>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t('admin.emailTemplates.test')}
        description={t('admin.emailTemplates.testLead')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={dirty || recipients.length > 5 || send.isPending}
              onClick={() => send.mutate(undefined)}
            >
              {t('admin.emailTemplates.testSend')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {dirty && <p className="text-sm text-amber">{t('admin.emailTemplates.testSaveFirst')}</p>}
          <Field
            label={t('admin.emailTemplates.testTo')}
            optionalLabel={t('common.optional')}
            description={t('admin.emailTemplates.testToLead')}
            help={<HelpTip k="email_templates.test_to" />}
            error={fieldError(send.error, 'to')}
          >
            {(p) => (
              <Input
                {...p}
                value={to}
                spellCheck={false}
                autoComplete="off"
                onChange={(e) => setTo(e.target.value)}
              />
            )}
          </Field>
        </div>
      </Dialog>
    </>
  );
}
